import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { bootstrapWorkshopAuth, getAuthToken, getBaseUrl, rewriteLegacyApiUrl } from "./lib/apiClient";

/**
 * Global fetch patch:
 * 1) Rewrite leftover :8080 / :8000 hardcodes → unified Aura FastAPI base
 * 2) Attach Authorization Bearer for our API (JWT or workshop/anon key)
 */
const originalFetch = window.fetch.bind(window);

function isUnifiedApiUrl(url: string): boolean {
  const base = getBaseUrl().replace(/\/$/, "");
  if (url.startsWith(base)) return true;
  try {
    const u = new URL(url, window.location.origin);
    return (
      u.port === "8001" ||
      u.hostname.includes("railway.app") ||
      u.pathname.startsWith("/api/")
    );
  } catch {
    return url.includes("/api/");
  }
}

window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
  const originalUrl =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input instanceof Request
          ? input.url
          : String(input);

  const url = rewriteLegacyApiUrl(originalUrl);
  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined)
  );

  if (isUnifiedApiUrl(url) && !headers.has("Authorization")) {
    const isAuthBootstrap = /\/api\/auth\/(login|config|owner-session)\/?(\?|$)/.test(url);
    if (!isAuthBootstrap) {
      headers.set("Authorization", `Bearer ${getAuthToken()}`);
    }
  }

  if (typeof input === "string" || input instanceof URL) {
    return originalFetch(url, { ...init, headers });
  }

  if (input instanceof Request) {
    if (url !== input.url || (isUnifiedApiUrl(url) && !input.headers.has("Authorization"))) {
      return originalFetch(new Request(url, input), { ...init, headers });
    }
    return originalFetch(input, { ...init, headers });
  }

  return originalFetch(input as RequestInfo, { ...init, headers });
};

bootstrapWorkshopAuth().finally(() => {
  createRoot(document.getElementById("root")!).render(<App />);
});
